<template>
  <div>
    <updated-list-component
      :table-headers="getTableHeaders()"
      :table-data="generatedTableData"
      @open-feedback-modal="onEditFeedbackListener($event)"
      @ask-for-feedback="askClientForFeedback($event)"
    />
    <template v-if="clientData && selectedFeedbackData">
      <template v-if="!isMobile">
        <v-dialog
          v-model="showMobileFeedbackModal"
          :width="modal.xlarge_width"
          :max-width="modal.xlarge_maxWidth"
          persistent
        >
          <feedback-component
            :property-data="selectedProperty"
            :feedback-data="selectedFeedbackData"
            @on-feedback-updated="updateFeedback($event)"
            @close="closeFeedbackDetailsModal"
          />
        </v-dialog>
      </template>
      <template v-else>
        <feedback-component
          v-if="showMobileFeedbackModal"
          class="mobile-feedback-component"
          :property-data="selectedProperty"
          :feedback-data="selectedFeedbackData"
          @on-feedback-updated="updateFeedback($event)"
          @close="closeFeedbackDetailsModal"
        />
      </template>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState, mapMutations } from 'vuex'
import FeedbackComponent from '../../feedback/FeedbackComponent'
import UpdatedListComponent from '../../uiUtils/UpdatedListComponent.vue'
import {
  getWantNeedMatch,
  getPropertyKeyValue,
  calculateFeedbackMatch
} from '../../../utils/propertyUtils'
import {
  getPropertyById,
  createFeedbackRequest
} from '../../../services/propertiesService'
import { askClientForFeedback } from '../../../services/messagesService'

import { dateOfLastFeedback } from '../../../services/invite-service'
import { isEmpty } from '../../../utils/generalUtils'

export default {
  filters: {
    dateFormat: value => {
      return moment(value).format('MM-DD-YY, hh:mm a')
    }
  },

  components: { UpdatedListComponent, FeedbackComponent },

  props: {
    tableData: {
      type: [Array, Object],
      required: true
    },
    feedbackType: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      displayPropertyModal: false,
      selectedProperty: null,
      selectedFeedbackData: null,
      generatedTableData: [],
      viewingId: null
    }
  },

  computed: {
    ...mapState('agent', ['agentData']),
    ...mapState('client', ['clientFavorites', 'clientData']),
    ...mapState('common', ['isMobile', 'showMobileFeedbackModal']),
    ...mapState('style', ['modal'])
  },

  watch: {
    tableData(newValue, oldValue) {
      this.generateTableData(newValue)
    }
  },

  mounted() {
    this.generateTableData(this.tableData)
  },

  methods: {
    ...mapMutations('common', {
      setSelectedHome: 'SET_SELECTED_HOME',
      setPersonName: 'SET_PERSON_NAME',
      setShowMobileFeedbackModal: 'SET_SHOW_MOBILE_FEEDBACK_MODAL'
    }),
    ...mapMutations('mobile', {
      setShowingMobileBackButton: 'SET_SHOWING_MOBILE_BACK_BUTTON'
    }),
    generateTableData(data) {
      if (this.isMobile) {
        this.generatedTableData = this.generateMobileTableItems(data)
      } else {
        this.generatedTableData = this.generateTableItems(data)
      }
    },

    updateFeedback(feedback) {
      this.selectedFeedbackData = feedback
    },

    getTableHeaders() {
      if (this.isMobile) {
        return [
          {
            text: 'Address',
            value: 'address',
            width: this.feedbackType !== 'provided' ? '60%' : '70%',
            sortable: false
          },
          {
            text: 'Feedback',
            value: 'feedback',
            sortable: false,
            align: 'center'
          }
        ]
      }
      let headers = [
        { text: 'Address', value: 'address', width: '50%', sortable: false },
        { text: 'List Price', value: 'list_price', sortable: false, align: 'center' },
        { text: 'Needs', value: 'needs', sortable: false, align: 'center' },
        { text: 'Wants', value: 'wants', sortable: false, align: 'center' },
        { text: 'Pics', value: 'pics', sortable: false, align: 'center' },
        { text: 'Date Viewed', value: 'last_viewed', sortable: false, align: 'center' }
      ]

      if (this.feedbackType !== 'provided') {
        headers.push({
          text: 'Provide',
          value: 'provideFeedback',
          sortable: false,
          align: 'center'
        })
      }
      headers.push({
        text: 'Feedback',
        value: 'feedback',
        sortable: false,
        align: 'center'
      })
      return headers
    },

    async onEditFeedbackListener(data = null) {
      this.selectedFeedbackData = data
      this.selectedProperty = (await getPropertyById(data.propertyId)).data
      this.setPersonName(this.agentData.name)
      this.setShowMobileFeedbackModal(true)
      this.setSelectedHome(data)
    },

    closeFeedbackDetailsModal() {
      this.selectedFeedbackData = null
      this.setPersonName(null)
      this.setShowMobileFeedbackModal(false)
      this.setSelectedHome(null)
    },

    destroyed() {
      this.setSelectedHome(null)
      this.setShowMobileFeedbackModal(false)
      this.setShowingMobileBackButton(false)
    },

    generateTableItems(tableDataArray) {
      let response = []

      if (this.feedbackType !== 'combine') {
        tableDataArray.sort(this.compare)
        tableDataArray.map((item, index) => {
          let rowObject = {}
          rowObject = this.addFirstColumn(item, rowObject)
          if (this.feedbackType === 'provided') {
            rowObject = this.addProvidedProperty(item, rowObject)
          } else {
            rowObject = this.addNeededProperty(item, rowObject)
          }
          rowObject = this.addLastColumns(item, rowObject)

          response.push(rowObject)
        })
      } else {
        tableDataArray.needed.map((item, index) => {
          let rowObject = {}
          rowObject = this.addFirstColumn(item, rowObject)
          rowObject = this.addNeededProperty(item, rowObject)
          rowObject = this.addLastColumns(item, rowObject)
          response.push(rowObject)
        })
        tableDataArray.provided.sort(this.compare)
        tableDataArray.provided.map((item, index) => {
          let rowObject = {}
          rowObject = this.addFirstColumn(item, rowObject)
          rowObject = this.addProvidedProperty(item, rowObject)
          rowObject = this.addLastColumns(item, rowObject)
          response.push(rowObject)
        })
      }

      return response
    },

    generateMobileTableItems(tableDataArray) {
      let response = []
      if (this.feedbackType !== 'combine') {
        tableDataArray.sort(this.compare)
        tableDataArray.map((item, index) => {
          let rowObject = {}
          rowObject = this.addFirstColumn(item, rowObject)
          if (this.feedbackType === 'provided') {
            rowObject = this.addProvidedMobileProperty(item, rowObject)
          } else {
            rowObject = this.addNeededMobileProperty(item, rowObject)
          }
          response.push(rowObject)
        })
      } else {
        tableDataArray.needed.map((item, index) => {
          let rowObject = {}
          rowObject = this.addFirstColumn(item, rowObject)
          response.push(rowObject)
        })
        tableDataArray.provided.sort(this.compare)
        tableDataArray.provided.map((item, index) => {
          let rowObject = {}
          rowObject = this.addFirstColumn(item, rowObject)
          response.push(rowObject)
        })
      }

      return response
    },

    addFirstColumn(item, rowObject) {
      rowObject[0] = {
        type: 'addressImg',
        icon: false,
        text: this.getHomeAddress(item.propertyId),
        cellClass: this.isMobile ? 'pl-1 pr-0' : '',
        data: item
      }
      return rowObject
    },

    addNeededProperty(item, rowObject) {
      rowObject[1] = { text: this.getListPrice(item) }
      rowObject[2] = { text: '-' }
      rowObject[3] = { text: '-' }
      rowObject[6] = {
        type: 'clickable',
        text: 'PROVIDE',
        class: 'hwayzSun',
        data: {
          type: 'feedback',
          value: item,
          action: 'open-feedback-modal'
        }
      }

      rowObject[7] = {
        type: 'clickable',
        text:
          item.feedback && dateOfLastFeedback(item.feedback.feedbackRequested)
            ? 'REQUESTED'
            : 'REQUEST',
        class:
          item.feedback && dateOfLastFeedback(item.feedback.feedbackRequested)
            ? 'hwayzDarkenGreen'
            : 'hwayzTeal',
        data: {
          value: {
            ...item,
            ...{ address: this.getHomeAddress(item.propertyId) }
          },
          action: 'ask-for-feedback'
        }
      }
      return rowObject
    },

    addNeededMobileProperty(item, rowObject) {
      rowObject[2] = {
        type: 'multiple',
        cellClass: this.isMobile ? 'px-1' : '',
        dataArray: [
          {
            type: 'clickable',
            text: 'PROVIDE',
            class: 'provide-feedback-btn hwayzSun',
            data: {
              type: 'feedback',
              value: item,
              action: 'open-feedback-modal'
            }
          },
          {
            type: 'clickable',
            text:
              item.feedback &&
              dateOfLastFeedback(item.feedback.feedbackRequested)
                ? 'REQUESTED'
                : 'REQUEST',
            class:
              item.feedback &&
              dateOfLastFeedback(item.feedback.feedbackRequested)
                ? 'provide-feedback-btn hwayzDarkenGreen'
                : 'provide-feedback-btn hwayzTeal',
            data: {
              value: {
                ...item,
                ...{ address: this.getHomeAddress(item.propertyId) }
              },
              action: 'ask-for-feedback'
            }
          }
        ]
      }
      return rowObject
    },

    addProvidedMobileProperty(item, rowObject) {
      rowObject[2] = {
        type: 'clickable',
        text: this.getMatchPercentage(item),
        data: { value: item, type: 'match', action: 'open-feedback-modal' }
      }
      return rowObject
    },

    addProvidedProperty(item, rowObject) {
      rowObject[2] = {
        text: `${Math.round(getWantNeedMatch(item, 'need'))}%`
      }
      rowObject[3] = {
        text: `${Math.round(getWantNeedMatch(item, 'want'))}%`
      }

      let matchIndex = 6
      if (this.feedbackType === 'combine') {
        rowObject[6] = { text: '-' }
        matchIndex = 7
      }

      rowObject[matchIndex] = {
        type: 'clickable',
        text: this.getMatchPercentage(item),
        data: { value: item, type: 'match', action: 'open-feedback-modal' }
      }
      return rowObject
    },

    addLastColumns(item, rowObject) {
      const picStatus = item.feedback && !isEmpty(item.feedback.media)
      rowObject[4] = { type: 'picIndicator', picStatus }
      rowObject[5] = { type: 'date', text: item._timestamp }
      return rowObject
    },

    /**
     * Retrieve home details based on home id
     */
    getHomeAddress(homeId) {
      const value = getPropertyKeyValue(
        homeId,
        this.clientFavorites,
        'Address'
      )
      return value || homeId
    },

    getListPrice(homeId) {
      const value = getPropertyKeyValue(
        homeId,
        this.clientFavorites,
        'ListPrice'
      )
      return value || homeId
    },

    compare(a, b) {
      if (a.type || b.type) {
        return -1
      } else {
        if (
          parseInt(this.getMatchPercentage(a)) >
          parseInt(this.getMatchPercentage(b))
        ) {
          return -1
        } else {
          return 1
        }
      }
    },

    getHomeDetails(homeId = null) {
      if (this.clientFavorites) {
        const foundHome = this.clientFavorites.find(
          home => home._id === homeId
        )
        if (foundHome) {
          return foundHome
        }
      }
      return homeId
    },

    /**
     * Returns the match percentage based on wants and needs
     * provided in the feedback by the client
     */
    getMatchPercentage(home) {
      if (home.feedback.match) {
        return calculateFeedbackMatch(home.feedback.match)
      }
      return '9999.99'
    },

    async askClientForFeedback(data) {
      const propertyInfo = (await getPropertyById(data.propertyId)).data
      if (
        data.feedback &&
        dateOfLastFeedback(data.feedback.feedbackRequested)
      ) {
        return
      }
      await askClientForFeedback({
        address: `${propertyInfo['Address']} ${propertyInfo['City']} ${propertyInfo['PostalCode']}`,
        to: [this.clientData._id],
        propertyId: data.propertyId
      })
      await createFeedbackRequest(this.clientData._id, data._id)
      data.feedback.feedbackRequested = new Date()
      this.generateTableData(this.tableData)
    }
  }
}
</script>

<style lang="sass">
.feedback-modal__card.fixed-modal
  position: fixed
  top: 47px
  left: 0
.provide-feedback-btn
  height: 28px
  width: 90px
  padding: 0 10px
  display: flex
  justify-content: center
  align-items: center
  border-radius: 20px
  margin: 0 auto
  &.hwayzSun
    border: 1px solid $hwayzSun
    margin-bottom: 12px
  &.hwayzDarkenGreen
    border: 1px solid $hwayzDarkenGreen
  &.hwayzTeal
    border: 1px solid $hwayzTeal
  @media only screen and (max-width: 360px)
    height: 24px
    width: 80px
    font-size: 1.3rem
    &.hwayzSun
      margin-bottom: 10px
// .address-image span
//   @media only screen and (max-width: 450px)
//     width: 110px
</style>
